import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import AttachFile from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useCallback, useEffect, useState } from 'react';

import { EngagementChannelType } from '@/__generated__/graphql';
import Avatar from '@/auth/components/Avatar';
import Button from '@/componentsv2/Button';
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';
import { handleFileUploadError, openFileDialog } from '@/core/attachment';
import { useApp } from '@/hooks/useAppContext';

import { formatFileSize, formatRelative } from '../components/EngagementHub/format';
import type { ViewerRole } from '../components/EngagementHub/roles';
import { canSendMessageFor } from '../components/EngagementHub/roles';
import Section from './Section';
import {
  CHANNEL_PRIVACY,
  channelLabel,
  openableDirectChannels,
  selectableChannels,
} from './channels';
import {
  ENGAGEMENT_ATTACHMENT_URL,
  ENGAGEMENT_HUB,
  ENGAGEMENT_MESSAGES,
  OPEN_ENGAGEMENT_DIRECT_CHANNEL,
  SEND_ENGAGEMENT_MESSAGE,
} from './operations';
import { channelRosterLabel, partyIdentity } from './parties';
import { useSelfIdentity } from './session';
import type { EngagementView } from './viewModel';
import { ENGAGEMENT_QUEUE_IDS, engagementChannelResource } from './ws';

/**
 * §9.8's file chip: icon, filename, size. Both halves of the label are
 * best-effort — Sendbird promises only the URL — so an unnamed file falls back
 * to a neutral word and an unsized one renders no size at all rather than
 * "0 B".
 *
 * A button rather than the prototype's anchor: the URL is presigned on click,
 * so there is no href to put on the element at render time. That presign is
 * broken for every viewer today (ENG-5709); naming the file is the half this
 * ticket can deliver regardless.
 */
function AttachmentChip({
  filename,
  sizeBytes,
  onOpen,
}: {
  filename: string | null;
  sizeBytes: number | null;
  onOpen: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      // `min-h-48`, not `min-h-44`: 44 is absent from this config's spacing
      // scale, so `min-h-44` emits no CSS and the touch target never applies.
      // `min(260px, 100%)`, not a bare `260px`: the prototype's 260 is a cap on
      // a WIDE column, and at 375px the message column is only ~218px, so the
      // cap stopped binding before the parent did and a long filename pushed the
      // chip 42px past the panel. Measured in Storybook at 375, not reasoned.
      className="mt-8 inline-flex min-h-48 max-w-[min(260px,100%)] items-center gap-6 rounded-md border border-surface-border bg-surface-elevated px-12 py-6 text-on-surface util-caption hover:bg-surface-secondary"
    >
      <InsertDriveFileOutlined
        sx={{ fontSize: 16 }}
        className="shrink-0 text-on-surface-muted"
        aria-hidden
      />
      <span className="truncate font-medium">{filename ?? 'Attachment'}</span>
      {sizeBytes ? (
        <span className="shrink-0 text-on-surface-muted">{formatFileSize(sizeBytes)}</span>
      ) : null}
    </button>
  );
}

interface MessagesPanelProps {
  view: EngagementView;
  viewerRole: ViewerRole;
  viewerUserId: string;
  now: Date;
}

export default function MessagesPanel({
  view,
  viewerRole,
  viewerUserId,
  now,
}: MessagesPanelProps): JSX.Element {
  const { apiWebSocket, graphql } = useApp();
  const self = useSelfIdentity();
  const channels = selectableChannels(view.messageChannels, viewerRole);
  const openable = openableDirectChannels(view.messageChannels, viewerRole);
  // A ghost pill is an invitation to send a first message, so it is meaningless
  // on an engagement that can no longer take one — the gate the prototype puts
  // on its own ghost pills. Channels that already exist stay selectable, and
  // read-only, either way.
  const canOpen = canSendMessageFor(view.status, viewerRole);
  const pills: { type: EngagementChannelType; ghost: boolean }[] = [
    ...channels.map((type) => ({ type, ghost: false })),
    ...(canOpen ? openable.map((type) => ({ type, ghost: true })) : []),
  ];
  const [channelType, setChannelType] = useState<EngagementChannelType>(
    channels[0] ?? EngagementChannelType.Trilateral
  );
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [channelForbidden, setChannelForbidden] = useState(false);
  // The roster doubles as the name table: a sender who is neither on the project
  // nor the expert is from OnFrontiers, and the roster is where their name is.
  const members = view.messageChannels.find((c) => c.channelType === channelType)?.members ?? [];
  const identify = partyIdentity(view, viewerUserId, members, self);
  const rosterLabel = channelRosterLabel(members, viewerUserId);

  const { data, loading, error, refetch, fetchMore } = useQuery(ENGAGEMENT_MESSAGES, {
    variables: { engagementId: view.id, channelType },
  });
  const client = useApolloClient();
  // Re-read the messages AND the roster, because they are two different queries
  // and the roster is the one that carries the names.
  //
  // `members` above comes off the `view` prop, i.e. ENGAGEMENT_HUB. A sender who
  // is neither on the project nor the expert has NO roster row until they write
  // — that is the whole premise of "name them once they use it" — so refreshing
  // only ENGAGEMENT_MESSAGES renders their message with the roster that predates
  // them, and `nameOf` cannot name them until the next full page load. The same
  // applies to a message arriving over the websocket from someone new.
  const refreshMessagesAndRoster = useCallback(async () => {
    await Promise.all([refetch(), client.refetchQueries({ include: [ENGAGEMENT_HUB] })]);
  }, [client, refetch]);
  const [sendMessage, { loading: sending }] = useMutation(SEND_ENGAGEMENT_MESSAGE);
  const [loadAttachmentUrl] = useLazyQuery(ENGAGEMENT_ATTACHMENT_URL);
  const [openDirectChannel, { loading: opening }] = useMutation(OPEN_ENGAGEMENT_DIRECT_CHANNEL);
  const [openError, setOpenError] = useState<string | null>(null);

  // Payloads carry no body and no amounts by contract, so an event is only a
  // signal to re-read. Resubscribes on channel switch; the `on()` disposer
  // unsubscribes when this is the last listener for the resource.
  useEffect(() => {
    const off = apiWebSocket.on(
      ENGAGEMENT_QUEUE_IDS.NEW_MESSAGE,
      engagementChannelResource(view.id, channelType),
      () => void refreshMessagesAndRoster()
    );
    return off;
  }, [apiWebSocket, view.id, channelType, refreshMessagesAndRoster]);

  const messages = data?.engagementMessages.messages ?? [];
  const nextCursor = data?.engagementMessages.nextCursor ?? null;
  const canSend = canSendMessageFor(view.status, viewerRole) && !channelForbidden;
  // `channel_not_found` is the wall answering "nothing here" — for a channel
  // that is lazily created on first send, that IS the empty state. Anything else
  // is a real failure worth a retry.
  const readFailed =
    error !== undefined && firstErrorCode(error) !== ERROR_CODES.ENGAGEMENT_CHANNEL_NOT_FOUND;

  async function handleSend(attachment?: { url: string; filename?: string; sizeBytes?: number }) {
    const text = draft.trim();
    if (!text && !attachment) return;
    setSendError(null);
    try {
      await sendMessage({
        variables: {
          input: {
            engagementId: view.id,
            channelType,
            text: text || null,
            attachmentFileUrl: attachment?.url,
            // `||` on the filename, `??` on the size, and the difference is
            // deliberate: an empty filename describes nothing and is dropped,
            // but 0 is a legitimate size for a real file, so it is sent.
            attachmentFilename: attachment?.filename || undefined,
            attachmentSizeBytes: attachment?.sizeBytes ?? undefined,
          },
        },
      });
      setDraft('');
    } catch (err) {
      // The server is the authority on channel access: a forbidden send retires
      // the composer even though the client-side gate allowed it.
      if (firstErrorCode(err) === ERROR_CODES.ENGAGEMENT_CHANNEL_FORBIDDEN) {
        setChannelForbidden(true);
      }
      setSendError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'Your message could not be sent.'
      );
      return;
    }

    // Outside the try, deliberately. The send has already succeeded by here, so
    // a failure refreshing the view is not a send failure: reporting it as one
    // told the user "Your message could not be sent" about a message that WAS
    // sent, and a coincidental channel_forbidden code would have retired the
    // composer too. A stale panel is the honest degradation -- the websocket or
    // the next send will re-read it.
    try {
      await refreshMessagesAndRoster();
    } catch {
      // Intentionally swallowed; see above.
    }
  }

  /**
   * Open a bilateral channel with OnFrontiers, then select it.
   *
   * The re-read is not optional. `selectableChannels` renders from
   * `messageChannels` on the hub query, which is where the wall filtering
   * happens, so a channel this mutation just created is absent from the cached
   * copy and the pill it should appear as would not exist. Selecting it before
   * the re-read lands would show the composer for a channel the picker does not
   * list yet.
   */
  async function handleOpenChannel(type: EngagementChannelType) {
    setOpenError(null);
    try {
      await openDirectChannel({
        variables: { input: { engagementId: view.id, channelType: type } },
      });
      await client.refetchQueries({ include: [ENGAGEMENT_HUB] });
      // This IS a channel switch, so it retires the same errors the pill's
      // other branch does. `sendError` was the one left behind: a send that
      // failed on the trilateral thread stayed under the composer of the
      // bilateral channel this just opened, describing a channel the viewer
      // had already moved away from.
      setChannelType(type);
      setChannelForbidden(false);
      setSendError(null);
    } catch (err) {
      setOpenError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'That channel could not be opened.'
      );
    }
  }

  async function handleAttach() {
    const files = await openFileDialog(graphql.client, { maxSize: 10 * 1024 * 1024 }).catch(
      (err: unknown) => {
        setSendError(handleFileUploadError(err));
      }
    );
    const file = files?.[0];
    if (file?.url) {
      await handleSend({ url: file.url, filename: file.filename, sizeBytes: file.size });
    }
  }

  async function handleOpenAttachment(messageId: string) {
    const { data: urlData } = await loadAttachmentUrl({
      variables: { engagementId: view.id, channelType, messageId },
    });
    const url = urlData?.engagementAttachmentUrl;
    // Presigned host must never receive `window.opener`.
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <Section title="Messages">
      {/* One row for both kinds of channel (ENG-5732). A channel the viewer has
          not opened is a GHOST pill — `+` prefixed and dashed, per the prototype
          — rather than the separate card this used to carry. The lock marks
          private, the dash marks not-yet-opened; they are different signals and
          a pill can wear either, both, or neither.

          Unconditional, as the prototype has it. The old `> 1` guard meant the
          row's presence depended on how many channels happened to exist, so a
          viewer with nothing to switch to never read `Engagement chat` at all —
          and `pills` always holds the trilateral thread, so there is no empty
          row to guard against. */}
      <div className="mb-16 flex flex-wrap gap-8" role="group" aria-label="Channels">
        {pills.map(({ type, ghost }) => {
          const selected = !ghost && type === channelType;
          const isDirect = type !== EngagementChannelType.Trilateral;
          // The lock and the dash are decorative, so a screen reader gets the
          // same two facts as text. Not the name: the pills are queried and
          // announced by their label, and `CHANNEL_PRIVACY[channelType]` below
          // already states who can read the selected channel.
          const describedBy = isDirect ? `channel-pill-desc-${type}` : undefined;
          const tone = selected
            ? 'border-brand-primary bg-brand-light text-on-surface'
            : ghost
              ? 'border-dashed border-surface-border text-on-surface-muted'
              : 'border-surface-border text-on-surface-muted';
          return (
            <button
              key={type}
              type="button"
              // A ghost is an action, not a toggle: it creates the channel. Left
              // as `aria-pressed={selected}` it announced as a toggle that is
              // permanently unpressed, since `selected` is false by definition.
              aria-pressed={ghost ? undefined : selected}
              aria-describedby={describedBy}
              // Every pill, not just the ghost. Disabling only the ghost let a
              // click on another pill land mid-mutation, and the tail of
              // `handleOpenChannel` then re-applied its own `type`, snapping the
              // selection back to a channel the viewer had navigated away from.
              disabled={opening}
              onClick={() => {
                // A ghost pill is not a filter: clicking it creates the
                // channel, then selects it. `handleOpenChannel` owns both.
                if (ghost) {
                  void handleOpenChannel(type);
                  return;
                }
                // Every error here describes the channel that produced it, so
                // switching channel retires all three. `openError` was the one
                // that survived, leaving a failed open reported against a
                // channel the viewer had already moved away from.
                setChannelType(type);
                setChannelForbidden(false);
                setSendError(null);
                setOpenError(null);
              }}
              // `min-h-48`, not `min-h-44`: 44 is absent from this config's
              // spacing scale (…40, 48…), so `min-h-44` emits no CSS at all and
              // the touch target these controls were given never applied.
              className={`inline-flex min-h-48 items-center gap-6 rounded-full border px-16 py-8 body-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${tone}`}
            >
              {isDirect ? <LockOutlined sx={{ fontSize: 14 }} aria-hidden /> : null}
              <span>
                {ghost ? `+ ${channelLabel(type, viewerRole)}` : channelLabel(type, viewerRole)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Outside the buttons on purpose. Inside, this text joins the accessible
          NAME and every pill starts announcing as "Direct with OnFrontiers
          private channel"; as a description it is read after the name, which is
          where a qualifier belongs. */}
      <div className="sr-only">
        {pills
          .filter(({ type }) => type !== EngagementChannelType.Trilateral)
          .map(({ type, ghost }) => (
            <span key={type} id={`channel-pill-desc-${type}`}>
              {ghost ? 'Private channel, not opened yet.' : 'Private channel.'}
            </span>
          ))}
      </div>

      {/* Who is in the channel, above the transcript rather than beside the
          composer: it has to be readable before the first word is typed, not
          after. Always rendered, including for the trilateral thread, so that
          its absence never becomes the signal for "this one is private".

          Both this line and the roster line below are live-tree additions that
          the §9.8 prototype does not have, and both are deliberate (ENG-5746).
          The prototype scenes are authored with the wall already understood by
          the reader; a real customer arrives at a tab with three channels and no
          explanation of who can read which. Saying it is the cheapest way to
          stop someone writing something for OnFrontiers into the thread the
          expert reads. */}
      <p className="mb-8 text-on-surface-muted body-2">{CHANNEL_PRIVACY[channelType]}</p>

      {/* The roster line is the same disclosure from the other direction: staff
          read every channel without leaving a row, so this can only ever say
          MORE than silence does, never less. See `channelRosterLabel`. */}
      {rosterLabel ? <p className="mb-16 text-on-surface-muted body-2">{rosterLabel}</p> : null}

      {openError ? (
        <p className="mb-16 text-red-600 body-2" role="alert">
          {openError}
        </p>
      ) : null}

      {nextCursor ? (
        <div className="mb-16">
          <Button
            variant="secondary"
            onClick={() =>
              void fetchMore({
                variables: { engagementId: view.id, channelType, cursor: nextCursor },
              })
            }
          >
            Load earlier messages
          </Button>
        </div>
      ) : null}

      {loading && !data ? (
        <p className="text-on-surface-muted body-2">Loading messages…</p>
      ) : readFailed ? (
        <div>
          <p className="text-on-surface body-2">We couldn’t load this conversation.</p>
          <div className="mt-8">
            <Button variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        </div>
      ) : messages.length === 0 ? (
        // A channel that doesn't exist yet reads the same as an empty one — the
        // wall answers denial with absence, so neither is an error (§5).
        <p className="text-on-surface-muted body-2">No messages yet.</p>
      ) : (
        <ul className="flex flex-col gap-16">
          {messages.map((m) => {
            // Null for a system message. It still gets a row and a neutral
            // avatar, but no name — "A participant" is reserved for a real
            // sender that nothing resolves.
            const sender = identify(m.senderId ?? '');
            return (
              <li key={m.messageId} className="flex gap-12">
                {/* Decorative. `Avatar` sets the image alt to the sender's
                    INITIALS and the name is rendered as text right beside it,
                    so an exposed avatar makes a screen reader announce
                    "RV, Robin Vale" on every message. */}
                <Avatar
                  aria-hidden
                  fullName={sender?.name ?? ''}
                  src={sender?.pictureUrl ?? undefined}
                  sx={{ width: 32, height: 32 }}
                />
                <div className="flex min-w-0 flex-1 flex-col items-start">
                  <div className="flex items-baseline gap-8">
                    {sender ? (
                      <span className="font-medium text-on-surface body-2">{sender.name}</span>
                    ) : null}
                    {/* Provenance AND a name of their own: a rep we cannot name
                        already reads "OnFrontiers", so the chip would print it
                        twice. The bubble below still marks them. */}
                    {sender?.isOnFrontiers && !sender.nameIsBrand ? (
                      <span className="rounded bg-surface-secondary px-4 py-2 text-on-surface-muted util-caption">
                        OnFrontiers
                      </span>
                    ) : null}
                    <span className="text-on-surface-muted body-2">
                      {formatRelative(String(m.sentAt), now)}
                    </span>
                  </div>
                  {m.text ? (
                    <p
                      className={`mt-4 max-w-[680px] whitespace-pre-line rounded-lg px-16 py-12 text-on-surface body-2 ${
                        sender?.isOnFrontiers
                          ? 'border border-yellow-700/40 bg-yellow-50 dark:bg-yellow-900/10'
                          : 'bg-surface-secondary'
                      }`}
                    >
                      {m.text}
                    </p>
                  ) : null}
                  {m.hasAttachment ? (
                    <AttachmentChip
                      filename={m.attachment?.filename ?? null}
                      sizeBytes={m.attachment?.sizeBytes ?? null}
                      onOpen={() => void handleOpenAttachment(m.messageId)}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canSend ? (
        <div className="mt-24 flex flex-col gap-8">
          <textarea
            value={draft}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
            rows={3}
            aria-label="Message"
            placeholder="Send a message…"
            className="w-full rounded-lg border border-surface-border bg-surface-card p-12 text-on-surface body-2"
          />
          {sendError ? <p className="text-red-600 body-2">{sendError}</p> : null}
          {/*
            Attach before Send, and both below the textarea on the right
            (ENG-5746). The order and the emphasis are the prototype's
            (`components/EngagementHub/MessagesTab.tsx#ComposerArea`): you attach
            while composing and send once, so Send is the primary and reading
            order puts it last.

            The PLACEMENT is a deliberate, product-approved deviation. The
            prototype puts both controls to the right OF the textarea on the same
            row, which costs the composer a third of its width on every viewport
            to hold two controls used once per message. Below-right is also what
            the platform's existing messaging composer does
            (`src/messaging/components/MessageInput.tsx`), so the two chat
            surfaces agree.
          */}
          <div className="flex justify-end gap-8">
            <Button
              variant="secondary"
              startIcon={AttachFile}
              onClick={() => void handleAttach()}
              disabled={sending}
            >
              Attach
            </Button>
            <Button onClick={() => void handleSend()} disabled={sending || !draft.trim()}>
              Send
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  );
}
